import { useState, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';

interface ProfileImageProps {
  src?: string;
  fallbackSrc?: string;
  username: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onRefresh?: () => void;
}

export function ProfileImage({
  src,
  fallbackSrc = '/default-profile.png',
  username,
  size = 'md',
  className = '',
  onRefresh,
}: ProfileImageProps) {
  const [imageSrc, setImageSrc] = useState<string | undefined>(src);
  const [imageError, setImageError] = useState<boolean>(false);
  const [supabaseImageUrl, setSupabaseImageUrl] = useState<string | null>(null);
  const [loadAttempts, setLoadAttempts] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  // Supabase에서 저장된 이미지 URL 가져오기
  useEffect(() => {
    const fetchSupabaseImage = async () => {
      if (!username) return;
      
      setIsLoading(true);
      
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData?.session?.user?.id) return;
        
        const { data, error } = await supabase
          .from('saved_instagram_accounts')
          .select('supabase_profile_pic_url, supabase_profile_hd_pic_url, profile_data')
          .eq('username', username)
          .eq('user_id', sessionData.session.user.id)
          .is('deleted_at', null)
          .maybeSingle();
        
        if (error) {
          console.error('프로필 이미지 조회 오류:', error);
          return;
        }
        
        // 프로필 이미지 URL 결정 로직
        if (data?.supabase_profile_hd_pic_url) {
          console.log('Supabase HD 이미지 사용:', username);
          setSupabaseImageUrl(data.supabase_profile_hd_pic_url);
        } else if (data?.supabase_profile_pic_url) {
          console.log('Supabase 일반 이미지 사용:', username);
          setSupabaseImageUrl(data.supabase_profile_pic_url);
        } else if (data?.profile_data?.profilePicUrlHD) {
          console.log('프로필 HD 이미지 사용:', username);
          setImageSrc(data.profile_data.profilePicUrlHD);
        } else if (data?.profile_data?.profilePicUrl) {
          console.log('프로필 일반 이미지 사용:', username);
          setImageSrc(data.profile_data.profilePicUrl);
        }
      } catch (error) {
        console.error('Supabase에서 이미지 URL 조회 실패:', error);
      } finally {
        // 작은 지연 후 로딩 상태 해제
        setTimeout(() => setIsLoading(false), 300);
      }
    };
    
    fetchSupabaseImage();
  }, [username]);

  // src prop이 변경되면 상태 업데이트 및 오류 상태 초기화
  useEffect(() => {
    if (src) {
      setIsLoading(true);
      setImageSrc(src);
      setImageError(false);
      setLoadAttempts(0);
      
      // 작은 지연 후 로딩 상태 해제
      setTimeout(() => setIsLoading(false), 300);
    }
  }, [src]);
  
  // Supabase 이미지 URL이 있으면 그걸 우선 사용
  useEffect(() => {
    if (supabaseImageUrl) {
      setIsLoading(true);
      setImageSrc(supabaseImageUrl);
      setImageError(false);
      setLoadAttempts(0);
      
      // 작은 지연 후 로딩 상태 해제
      setTimeout(() => setIsLoading(false), 300);
    }
  }, [supabaseImageUrl]);

  // 이미지 URL 유효성 검사 함수 개선
  const isValidImageUrl = (url?: string): boolean => {
    if (!url) return false;
    
    // 기본적인 URL 형식 검사
    try {
      new URL(url);
    } catch (e) {
      console.warn('유효하지 않은 URL 형식:', url);
      return false;
    }
    
    // http/https로 시작하고 최소 길이가 있는지 확인
    const isHttp = url.startsWith('http://') || url.startsWith('https://');
    const hasMinLength = url.length > 10;
    
    if (!isHttp || !hasMinLength) {
      console.warn('URL이 http(s)로 시작하지 않거나 너무 짧음:', url);
      return false;
    }
    
    // 금지된 URL 패턴 확인
    const forbiddenPrefixes = ['data:', 'blob:', 'javascript:'];
    if (forbiddenPrefixes.some(prefix => url.startsWith(prefix))) {
      console.warn('금지된 URL 프로토콜:', url);
      return false;
    }
    
    return true;
  };

  const sizeClass = {
    sm: 'h-10 w-10',
    md: 'h-16 w-16',
    lg: 'h-24 w-24',
  }[size];

  // 이미지 에러 처리 함수 개선
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.warn(`이미지 로드 실패 (시도 ${loadAttempts + 1}): ${imageSrc}`);
    setLoadAttempts(prev => prev + 1);
    setImageError(true);
    setIsLoading(false);
    
    // 최대 시도 횟수를 초과하면 폴백 이미지 사용
    if (loadAttempts >= 1) {
      console.log('최대 시도 횟수 초과, 폴백 이미지 사용');
      const target = e.target as HTMLImageElement;
      target.src = fallbackSrc;
      target.onerror = null; // 무한 루프 방지
    }
  };

  // 이미지 로드 완료 핸들러
  const handleImageLoaded = () => {
    setIsLoading(false);
  };

  // 이미지 새로고침 핸들러
  const handleRefresh = () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    setIsLoading(true);
    setImageError(false);
    setLoadAttempts(0);
    
    // 이미지 캐시를 무시하고 다시 로드하기 위한 임시 URL 생성
    const timestamp = new Date().getTime();
    if (supabaseImageUrl) {
      setImageSrc(`${supabaseImageUrl}?t=${timestamp}`);
    } else if (src) {
      setImageSrc(`${src}?t=${timestamp}`);
    }
    
    // 부모 컴포넌트의 새로고침 핸들러 호출 (있는 경우)
    if (onRefresh) {
      onRefresh();
    }
    
    // 애니메이션 효과를 위해 약간의 지연 후 상태 변경
    setTimeout(() => {
      setIsRefreshing(false);
      setIsLoading(false);
    }, 1000);
  };

  // 최종 이미지 URL 결정 - 유효하지 않으면 폴백 사용
  const finalImageSrc = (() => {
    if (imageError) return fallbackSrc;
    
    // 일반적인 유효성 검사
    if (isValidImageUrl(imageSrc)) return imageSrc;
    
    return fallbackSrc;
  })();

  // 로딩 중일 때 스켈레톤 표시
  if (isLoading) {
    return (
      <div className="relative">
        <Skeleton 
          className={`${sizeClass} rounded-full animate-pulse ${className}`} 
        />
      </div>
    );
  }

  return (
    <motion.div 
      className="relative group"
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <Avatar 
        className={`${sizeClass} ring-2 ring-primary/10 transition-shadow duration-300 ${isHovered ? 'ring-primary/30 shadow-lg' : ''} ${className}`}
      >
        <AvatarImage
          src={finalImageSrc}
          alt={username}
          onError={handleImageError}
          onLoad={handleImageLoaded}
          className="object-cover"
          loading="lazy"
        />
        <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white">
          {imageError ? (
            <AlertCircle className="h-4 w-4" />
          ) : (
            <User className="h-4 w-4" />
          )}
        </AvatarFallback>
      </Avatar>
      
      {/* 오류 시 배지 표시 */}
      {imageError && (
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-orange-500 flex items-center justify-center"
        >
          <AlertCircle className="h-2.5 w-2.5 text-white" />
        </motion.div>
      )}
      
      {/* 새로고침 버튼 - 호버 시 표시 */}
      {onRefresh && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ 
            opacity: isHovered ? 1 : 0,
            scale: isHovered ? 1 : 0.8
          }}
          transition={{ duration: 0.2 }}
        >
          <Button
            variant="ghost"
            size="icon"
            className={`absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary/80 text-primary-foreground shadow-md ${isRefreshing ? 'animate-spin' : ''}`}
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className="h-3 w-3 text-white" />
            <span className="sr-only">이미지 새로고침</span>
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
}